import { useLanderConfig } from "../commons/ConfigContext";
import { BannerType } from "../commons/banner";
import { LanderTemplate } from "../commons/constants/Templates";
import { ExperimentIds } from "../commons/constants/experiment";
import { LanderConfig } from "../types/LanderConfig";
export type AuctionArm = "control" | "treatmentB" | "treatmentC" | "none";

interface AuctionUrlBuildResult {
  url: string | null;
  error: boolean;
}

/** PARKING-5212: backend (#119) emits a fully-resolved, env-correct auction URL into
 * `experiment.data.auctionDetailsUrl`; frontend consumes it VERBATIM (no host, no id, no
 * client-side construction). Absent/empty/non-string/invalid → error, not a guessed URL (a
 * fabricated destination is a guaranteed 404). Side-effect-free (mirrors buildRsocRedirectUrl). */
export function buildAuctionDetailsUrl(
  landerConfig: LanderConfig
): AuctionUrlBuildResult {
  const url = landerConfig.experiment?.data?.auctionDetailsUrl;
  if (typeof url !== "string" || url === "") {
    return { url: null, error: true };
  }
  try {
    return { url: new URL(url).toString(), error: false };
  } catch {
    return { url: null, error: true };
  }
}

function deriveAuctionArm(cohort: string | undefined): AuctionArm {
  if (cohort === "treatment_b") return "treatmentB";
  if (cohort === "treatment_c") return "treatmentC";
  return "control";
}

export interface AuctionRedirectExperiment {
  variant: AuctionArm;
  ddUrl: string | null;
  ddUrlError: boolean;
  shouldRedirectToDetails: boolean;
}

/** PARKING-5212: pure selector for the auction-banner redirect experiment.
 * Fires no events, dispatches nothing; effects live in useAuctionArmEffects
 * and AuctionDetailsRedirect. */
export function useAuctionRedirectExperiment(): AuctionRedirectExperiment {
  const { landerConfig } = useLanderConfig();
  const { experiment: { key, cohort } = {} } = landerConfig;

  const inExperiment = key === ExperimentIds.auctionBannerRedirect;

  const template = landerConfig.lander?.template;
  const isParkweb =
    template === LanderTemplate.PARK_WEB ||
    template === LanderTemplate.PARK_WEB_HEG;

  const banner = landerConfig.lander?.banner;
  const isAuction = !!banner?.show && banner?.type === BannerType.AUCTION;

  const isEligible = inExperiment && isParkweb && isAuction;

  if (!isEligible) {
    return {
      variant: "none",
      ddUrl: null,
      ddUrlError: false,
      shouldRedirectToDetails: false,
    };
  }

  const variant = deriveAuctionArm(cohort);

  const { url: ddUrl, error: ddUrlError } =
    buildAuctionDetailsUrl(landerConfig);

  return {
    variant,
    ddUrl,
    ddUrlError,
    shouldRedirectToDetails: variant === "treatmentC" && !!ddUrl,
  };
}
